import {
  CAMERA_DEFAULT_Z,
  CAMERA_DEFAULT_ZOOM,
  CAMERA_MIN_ZOOM,
  PAN_VELOCITY_FACTOR,
  PLANE_DEFAULT_Z,
  ZOOM_STEP,
  SHOP_FLOOR_CAMERA_DEFAULT_ZOOM,
} from '@Components/BigBrowse/BigBrowseConfig';
import type {
  PlaneBoundaries,
  PlaneSize,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import type { TransientClass } from '@Utils/useClassStore';
import { ObservableProp } from '@Utils/useClassStore';
import type CameraControls from 'camera-controls';
import { injectable } from 'inversify';
import { action, computed, when } from 'mobx';
import type { RefObject } from 'react';

import container from '~ioc/ioc';

type ScrollBoundaryProvider = {
  scrollBoundary: PlaneBoundaries | undefined;
};

@injectable()
export class CanvasManager implements TransientClass {
  id = Math.floor(Math.random() * 1000);

  private scrollBoundaryProvider!: ScrollBoundaryProvider;
  private cameraControlsRef: RefObject<CameraControls> | null = null;

  private cameraOnPointerDown = { x: 0, y: 0 };
  private cameraOnPointerUp = { x: 0, y: 0 };

  cameraZoom = new ObservableProp(CAMERA_DEFAULT_ZOOM);
  private isAttachedToFloorView = new ObservableProp(false);

  init = ({
    scrollBoundaryProvider,
  }: {
    scrollBoundaryProvider: ScrollBoundaryProvider;
  }) => {
    this.scrollBoundaryProvider = scrollBoundaryProvider;
  };

  windowSize = new ObservableProp<PlaneSize>({ width: 0, height: 0 });
  /* cameraPosition should be used only from CameraControls components */
  _cameraPositionThree = new ObservableProp<[number, number, number]>([
    0, 0, 0,
  ]);

  getDefaultZoom(): number {
    if (!this.isAttachedToFloorView.value) {
      // no zoom for brandconcession/productSet views
      return CAMERA_DEFAULT_ZOOM;
    }

    if (this.windowSize.value.width > 800) {
      // we're looking at the shop floor inside
      // a decent-sized window, so use the defined
      // ideal default zoom for it
      return SHOP_FLOOR_CAMERA_DEFAULT_ZOOM;
    }

    // below 800 a single display does fitting in screen width,
    // so we use a zoom (width/900) that makes sure that a single display fits in screen width
    return Math.max(this.windowSize.value.width / 900, CAMERA_MIN_ZOOM);
  }

  setIsAttachedToFloorView = async (
    isAttachedToFloorView: boolean,
    resetZoom = true
  ) => {
    // need to wait for the HTML canvas to render before setting the zoom
    // because it needs the HTML canvas size to calculate it
    await when(() => this.windowSize.value.width > 0);

    this.isAttachedToFloorView.set(isAttachedToFloorView);
    if (resetZoom) {
      this.cameraZoom.set(this.getDefaultZoom());
    }
  };

  @computed get cameraPosition(): Position2D {
    return {
      x: this._cameraPositionThree.value[0],
      y: this._cameraPositionThree.value[1],
    };
  }

  @computed get cameraBoundaries(): PlaneBoundaries {
    const [x, y] = this._cameraPositionThree.value;
    const halfHeight = this.windowSize.value.height / this.cameraZoom.value / 2;
    const halfWidth = this.windowSize.value.width / this.cameraZoom.value / 2;

    return {
      top: y + halfHeight,
      bottom: y - halfHeight,
      left: x - halfWidth,
      right: x + halfWidth,
    };
  }

  @computed get cameraSpecs() {
    const { width, height } = this.windowSize.value;

    return {
      position: [0, 0, 2] as [number, number, number],
      left: -width / 2,
      right: width / 2,
      top: height / 2,
      bottom: -height / 2,
      near: 0,
      far: 3,
      zoom: this.cameraZoom.value,
      scale: [1, 1, 1] as [number, number, number],
    };
  }

  @computed get pinchZoomEnabled() {
    return this.isAttachedToFloorView.value;
  }

  @computed get scrollBoundary(): PlaneBoundaries | undefined {
    return this.scrollBoundaryProvider.scrollBoundary;
  }

  getPanDistance = (): number =>
    // if camera did not scroll between PointerDown and PointerUp result should be 0.
    Math.abs(
      this.cameraOnPointerDown.x -
        this.cameraOnPointerUp.x +
        this.cameraOnPointerDown.y -
        this.cameraOnPointerUp.y
    );

  bindCameraControlsRef = (
    cameraControlsRef: RefObject<CameraControls> | null
  ) => {
    this.cameraControlsRef = cameraControlsRef;
  };

  scrollTo = (position: [number, number, number], smooth = true) => {
    const [x, y] = position;
    this.truckTo(x, y, smooth).then();
  };

  handlePan = (velocityX: number, velocityY: number) => {
    this.truckByDelta(
      velocityX * -PAN_VELOCITY_FACTOR,
      velocityY * PAN_VELOCITY_FACTOR
    ).then();
  };

  @action onPointerDown = () => {
    const [x, y] = this._cameraPositionThree.value;
    this.cameraOnPointerDown = {
      x: Number(x.toFixed(0)),
      y: Number(y.toFixed(0)),
    };
  };

  @action onPointerUp = () => {
    const [x, y] = this._cameraPositionThree.value;
    this.cameraOnPointerUp = {
      x: Number(x.toFixed(0)),
      y: Number(y.toFixed(0)),
    };
  };

  destroy = () => {
    /* do nothing */
  };

  private truckTo = async (x: number, y: number, smooth = true) => {
    const effective = { x, y };

    if (this.scrollBoundary) {
      const { top, left, right, bottom } = this.scrollBoundary;
      effective.x = Math.min(Math.max(left, x), right);
      effective.y = Math.min(Math.max(bottom, y), top);
    }

    return this.cameraControlsRef?.current?.setLookAt(
      effective.x,
      effective.y,
      CAMERA_DEFAULT_Z,
      effective.x,
      effective.y,
      PLANE_DEFAULT_Z,
      smooth
    );
  };

  truckByDelta = (dx: number, dy: number, smooth = true) => {
    const [camX, camY] = this._cameraPositionThree.value;
    const { x, y } = { x: camX + dx, y: camY + dy };

    return this.truckTo(x, y, smooth);
  };

  zoomIn = () => {
    this.cameraControlsRef?.current?.zoomTo(
      this.cameraZoom.value + ZOOM_STEP,
      true
    );
  };

  zoomOut = () => {
    this.cameraControlsRef?.current?.zoomTo(
      this.cameraZoom.value - ZOOM_STEP,
      true
    );
  };
}

container.bind<CanvasManager>(CanvasManager).toSelf().inTransientScope();
